/**
 * Toast notifications for the viewer (same inject pattern as AudioPlayer/TrayChoice).
 *
 * Mounted inside `#ui-container` because that element is the WebXR dom-overlay
 * root: anything outside it is invisible while an immersive AR session runs.
 */

export type NotificationTone = 'error' | 'warning' | 'info';

export type NotificationOptions = {
  title: string;
  detail?: string;
  tone?: NotificationTone;
  /** Repeats of the same key only refresh the visible toast instead of stacking. */
  dedupeKey?: string;
};

const MAX_VISIBLE_NOTIFICATIONS = 3;
const LEAVE_ANIMATION_MS = 250;

const AUTO_DISMISS_MS: Record<NotificationTone, number> = {
  error: 12_000,
  warning: 9_000,
  info: 5_000,
};

type ResolvedNotification = Required<Pick<NotificationOptions, 'title' | 'tone' | 'dedupeKey'>> & {
  detail?: string;
};

type ActiveNotification = {
  element: HTMLElement;
  dedupeKey: string;
  tone: NotificationTone;
  dismissTimer: number;
};

const activeNotifications: ActiveNotification[] = [];
let notificationList: HTMLElement | null = null;

export function showNotification(options: NotificationOptions): void {
  const resolved = resolveNotification(options);
  const alreadyVisible = activeNotifications.find(
    (notification) => notification.dedupeKey === resolved.dedupeKey
  );
  if (alreadyVisible) {
    restartAutoDismiss(alreadyVisible);
    return;
  }

  const notification = renderNotification(resolved);
  activeNotifications.push(notification);
  dismissOldestWhileOverflowing();
}

export function dismissAllNotifications(): void {
  for (const notification of [...activeNotifications]) {
    dismissNotification(notification);
  }
}

function resolveNotification(options: NotificationOptions): ResolvedNotification {
  const tone = options.tone ?? 'error';
  return {
    title: options.title,
    detail: options.detail,
    tone,
    dedupeKey: options.dedupeKey ?? `${tone}|${options.title}|${options.detail ?? ''}`,
  };
}

function renderNotification(resolved: ResolvedNotification): ActiveNotification {
  const element = document.createElement('li');
  element.className = `spotar-notification spotar-notification--${resolved.tone}`;
  element.appendChild(createTextColumn(resolved));
  element.appendChild(createCloseButton(element));

  ensureNotificationList().appendChild(element);
  // Next frame, so the browser has a start value to transition from.
  requestAnimationFrame(() => element.classList.add('is-visible'));

  const notification: ActiveNotification = {
    element,
    dedupeKey: resolved.dedupeKey,
    tone: resolved.tone,
    dismissTimer: 0,
  };
  restartAutoDismiss(notification);
  return notification;
}

function createTextColumn(resolved: ResolvedNotification): HTMLElement {
  const column = document.createElement('div');
  column.className = 'spotar-notification__text';

  const title = document.createElement('p');
  title.className = 'spotar-notification__title';
  // textContent, never innerHTML: titles and details carry API/error strings.
  title.textContent = resolved.title;
  column.appendChild(title);

  if (!resolved.detail) return column;

  const detail = document.createElement('p');
  detail.className = 'spotar-notification__detail';
  detail.textContent = resolved.detail;
  column.appendChild(detail);
  return column;
}

function createCloseButton(element: HTMLElement): HTMLElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'spotar-notification__close';
  button.setAttribute('aria-label', 'Meldung schließen');
  button.textContent = '✕';
  button.addEventListener('click', () => {
    const notification = activeNotifications.find((entry) => entry.element === element);
    if (notification) dismissNotification(notification);
  });
  return button;
}

function restartAutoDismiss(notification: ActiveNotification): void {
  window.clearTimeout(notification.dismissTimer);
  notification.dismissTimer = window.setTimeout(
    () => dismissNotification(notification),
    AUTO_DISMISS_MS[notification.tone]
  );
}

function dismissOldestWhileOverflowing(): void {
  while (activeNotifications.length > MAX_VISIBLE_NOTIFICATIONS) {
    dismissNotification(activeNotifications[0]);
  }
}

function dismissNotification(notification: ActiveNotification): void {
  const index = activeNotifications.indexOf(notification);
  if (index === -1) return;
  activeNotifications.splice(index, 1);
  window.clearTimeout(notification.dismissTimer);
  notification.element.classList.remove('is-visible');
  window.setTimeout(() => notification.element.remove(), LEAVE_ANIMATION_MS);
}

function ensureNotificationList(): HTMLElement {
  if (notificationList) return notificationList;
  injectNotificationStyles();
  const list = document.createElement('ul');
  list.id = 'spotar-notifications';
  list.setAttribute('role', 'status');
  list.setAttribute('aria-live', 'polite');
  const overlayRoot = document.getElementById('ui-container') ?? document.body;
  overlayRoot.appendChild(list);
  notificationList = list;
  return list;
}

function injectNotificationStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    /* Both selectors beat "#ui-container > *", which would make the list catch taps. */
    #spotar-notifications,
    #ui-container > #spotar-notifications {
      position: fixed;
      top: 76px;
      left: 12px;
      right: 12px;
      z-index: 300;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 0;
      list-style: none;
      pointer-events: none;
    }
    .spotar-notification {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      width: 100%;
      max-width: 460px;
      padding: 12px 12px 12px 14px;
      border-radius: 12px;
      border-left: 4px solid var(--spotar-notification-accent);
      background-color: #ffffff;
      box-shadow: var(--spotar-ui-box-shadow);
      color: #111111;
      font-family: Roboto, sans-serif;
      text-align: left;
      opacity: 0;
      transform: translateY(-8px);
      transition: opacity ${LEAVE_ANIMATION_MS}ms ease, transform ${LEAVE_ANIMATION_MS}ms ease;
      pointer-events: auto;
    }
    .spotar-notification.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
    .spotar-notification--error { --spotar-notification-accent: #c62828; }
    .spotar-notification--warning { --spotar-notification-accent: #e08a1e; }
    .spotar-notification--info { --spotar-notification-accent: #1f6feb; }
    .spotar-notification__text {
      flex: 1;
      min-width: 0;
    }
    .spotar-notification__title {
      margin: 0;
      font-size: 15px;
      font-weight: 700;
      line-height: 1.35;
    }
    .spotar-notification__detail {
      margin: 4px 0 0;
      font-size: 12px;
      line-height: 1.45;
      color: #5a5a5a;
      overflow-wrap: anywhere;
    }
    .spotar-notification__close {
      flex: 0 0 auto;
      width: 28px;
      height: 28px;
      padding: 0;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: #5a5a5a;
      font-size: 14px;
      line-height: 1;
      cursor: pointer;
    }
    .spotar-notification__close:hover {
      background: rgba(0, 0, 0, 0.06);
      color: #111111;
    }
  `;
  document.head.appendChild(style);
}
